/**
 * Le monde — l'objet que l'application affiche, et le seul.
 *
 * Jusqu'ici chaque expérience possédait son état, sa page et sa vue, et changer
 * d'onglet démontait tout. Le produit annoncé par la spec de référence (§2.1)
 * est l'inverse : **un environnement, dans lequel on place des objets**, qu'on
 * regarde vivre et qu'on parcourt. Le monde est cet environnement ; les
 * scénarios ne sont plus que des états de départ qu'on y verse.
 *
 * ## Ce que ce module ajoute à `nbody`, et pourquoi c'est tout
 *
 * La physique est déjà là, validée par ses invariants. Ce module n'en écrit pas
 * une ligne de plus. Il ajoute trois choses que `nbody` n'a aucune raison de
 * connaître :
 *
 * - la **nature** d'un corps — étoile, planète, sonde. Elle ne change rien au
 *   calcul, qui ne connaît que des masses ; elle change ce qu'on dessine.
 * - le **temps écoulé**, compté en pas et non lu sur une horloge. C'est ce qui
 *   permet d'afficher « 3 ans » sans jamais demander l'heure à la machine
 *   (CLAUDE.md §5).
 * - l'**étendue**, dont la caméra a besoin pour cadrer sans rien couper.
 *
 * ## Ce qu'il ne fait pas
 *
 * Aucun repère imbriqué, aucune rotation : un corps posé sur une planète qui
 * tourne n'a pas encore de sens ici, et l'écrire avant d'en avoir l'usage
 * serait du code validé mais mort. Ça vient avec le gnomon.
 *
 * Et tout se passe dans un plan, comme l'intégrateur qu'il appelle.
 */

import type { Vector2 } from "./integrator.js";
import type { Quantity } from "./units.js";
import { advance, centreOfMass } from "./nbody.js";
import { max, sqrt } from "./math.js";
import { quantity, valueIn } from "./units.js";

/**
 * Ce qu'est un corps, du point de vue du dessin seulement.
 *
 * Le calcul ne fait aucune différence entre une étoile et un caillou : il n'y a
 * que des masses. Cette étiquette ne pèse donc sur rien de physique — elle dit
 * comment représenter le corps, et elle doit survivre à l'intégration, ce qu'un
 * banc vérifie.
 */
export type EntityKind = "star" | "planet" | "probe";

export interface Entity {
  readonly id: string;
  readonly label: string;
  readonly kind: EntityKind;
  readonly mass: Quantity<"mass">;
  /**
   * Rayon **vrai**, en mètres.
   *
   * Il n'entre dans aucun calcul : le moteur est un problème de points
   * matériels, et deux corps qui se rencontrent se traversent. Il n'est là que
   * pour être dessiné — mais il doit l'être fidèlement, sans quoi on ne peut
   * pas descendre voir une planète de près, ce qui est tout l'intérêt d'avoir
   * un monde plutôt que des pages.
   */
  readonly radius: Quantity<"length">;
  readonly position: Vector2<"length">;
  readonly velocity: Vector2<"velocity">;
}

export interface World {
  readonly entities: readonly Entity[];
  /** Temps simulé depuis le départ. Compté en pas, jamais lu sur une horloge. */
  readonly elapsed: Quantity<"time">;
}

/**
 * Le monde après `steps` pas de durée `step`.
 *
 * Le nombre de pas est une entrée explicite, et l'immuabilité fait le reste :
 * avancer de quarante puis de soixante donne **exactement** le même monde que
 * d'avancer de cent, au bit près. C'est ce qui garantit qu'une machine lente
 * voit la même trajectoire qu'une machine rapide, et le banc l'exige sans
 * tolérance.
 */
export function advanceWorld(
  world: World,
  step: Quantity<"time">,
  steps: Quantity<"dimensionless">,
): World {
  const moved = advance(world.entities, step, steps);

  return {
    // Le noyau ne lit pas ces métadonnées, mais son type générique les conserve
    // avec chaque corps : aucune jointure par position ne peut les désapparier.
    entities: moved,
    elapsed: quantity(
      valueIn(world.elapsed, "s") + valueIn(step, "s") * valueIn(steps, "1"),
      "s",
    ),
  };
}

export function entityById(world: World, id: string): Entity | undefined {
  return world.entities.find((one) => one.id === id);
}

/**
 * Rayon du monde autour de son barycentre.
 *
 * La caméra s'en sert pour cadrer. Le plancher n'est pas cosmétique : un monde
 * vide ou réduit à un point donnerait une étendue nulle, et une caméra placée à
 * une distance nulle ne regarde rien.
 */
export function worldExtent(world: World): Quantity<"length"> {
  const centre = centreOfMass(world.entities);
  const cx = valueIn(centre.x, "m");
  const cy = valueIn(centre.y, "m");

  let extent = 0;
  for (const one of world.entities) {
    const dx = valueIn(one.position.x, "m") - cx;
    const dy = valueIn(one.position.y, "m") - cy;
    extent = max(extent, sqrt(dx * dx + dy * dy));
  }

  return quantity(max(extent, 1), "m");
}
